/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Button from "uis/Button";
import Modal from "react-modal";
import { theme } from "styles/theme";
import SelectV2 from "uis/Select/select_v2";
import InputField from "uis/InputField";
import { useEffect, useState } from "react";
import Icon from "uis/Icon";
import { TVisit, TVisitor } from "apis/visitors/type";
import { isValidEmail, isValidPhoneNumber } from "../visitor-table";
import { reSendGuideMessage } from "apis/visitors/visitor";

const modalStyle = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        borderRadius: "20px",
        width: "max-content",
        transform: "translate(-50%, -50%)",
    },
    overlay: {
        backgroundColor: "var(--component-color-material-dimmer)",
        zIndex: 1,
    },
};

Modal.setAppElement("#root");

const Content = styled.p`
    color: ${theme.neutral600};
    font-size: 20px;
    font-weight: 400;
    line-height: 25px;
`;

const Title = styled.p`
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 12px;
    margin-top: 24px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 40px 30px;
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50px;
    background-color: #fff0f0;
`;

const UserList = styled.div`
    display: flex;
    flex-direction: column;
`;

type Props = {
    modalIsOpen: boolean;

    onHandleBtn?: () => void;
    sendVisitors?: TVisitor[];
};

const FailSendMessageModal = (props: Props) => {
    const { modalIsOpen, onHandleBtn, sendVisitors = [] } = props;
    const fails = [...sendVisitors]?.filter((item) => !item.isGuideMessageSent);
    const [sended, setSended] = useState<number>(0);

    return (
        <Modal isOpen={modalIsOpen} onRequestClose={() => {}} style={modalStyle}>
            <Container>
                <IconContainer>
                    <CircleCheckIcon />
                </IconContainer>
                <Title>방문자 등록 완료 후, 안내 메시지 전송 실패</Title>
                <Content>
                    {sendVisitors.length}건 중 {fails.length}건의 안내 메시지 전송이 실패되었습니다.
                </Content>
                <Header />
                <UserList>
                    {fails.map((item, index) => (
                        <UserItem key={index} visitor={item} onChange={() => setSended(sended + 1)} />
                    ))}
                </UserList>
                <Button
                    onClick={onHandleBtn}
                    title={sended === fails.length ? "다음" : "건너뛰기"}
                    styles={{
                        container: css`
                            width: 150px;
                            margin-left: auto;
                            padding: 16px;
                            margin-top: 36px;
                        `,
                    }}
                />
            </Container>
        </Modal>
    );
};

export default FailSendMessageModal;

const CircleCheckIcon = () => {
    return (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="36" height="36" rx="18" stroke="#FF3E3E" stroke-width="4" />
            <path
                d="M21.875 11.4286C21.875 10.6384 21.0371 10 20 10C18.9629 10 18.125 10.6384 18.125 11.4286V22.8571C18.125 23.6473 18.9629 24.2857 20 24.2857C21.0371 24.2857 21.875 23.6473 21.875 22.8571V11.4286ZM20 30C20.6216 30 21.2177 29.8119 21.6573 29.477C22.0968 29.1421 22.3438 28.6879 22.3438 28.2143C22.3438 27.7407 22.0968 27.2865 21.6573 26.9516C21.2177 26.6167 20.6216 26.4286 20 26.4286C19.3784 26.4286 18.7823 26.6167 18.3427 26.9516C17.9032 27.2865 17.6562 27.7407 17.6562 28.2143C17.6562 28.6879 17.9032 29.1421 18.3427 29.477C18.7823 29.8119 19.3784 30 20 30Z"
                fill="#FF3E3E"
            />
        </svg>
    );
};

const UserItemContainer = styled.div`
    display: flex;
    gap: 10px;
    border-bottom: 2px solid ${theme.neutral50};
    padding: 12px 0;
    align-items: center;
`;

const LoadingBtn = styled.div`
    width: 87px;
    display: flex;
    justify-content: center;
    height: 40px;
    align-items: center;
    background-color: ${theme.neutral50};
    border-radius: 8px;
    color: ${theme.neutral400};
    font-weight: 600;
`;

const ErrorText = styled.p`
    color: #ff3e3e;
    font-size: 14px;
`;

const UserItem = ({ visitor, onChange }: { visitor: TVisitor; onChange: () => void }) => {
    const [sendState, setSendState] = useState<"loading" | "finish" | "not_finish">("not_finish");
    const [visitorInfo, setVisitorInfo] = useState<TVisitor>(visitor);

    const isRightContact = () => {
        if (visitorInfo.guideChannel === "EMAIL") {
            if (isValidEmail(`${visitorInfo.email}`)) return true;
        } else {
            if (isValidPhoneNumber(`${visitorInfo.phoneNumber}`)) return true;
        }
    };

    return (
        <UserItemContainer>
            <p
                css={css`
                    font-weight: 500;
                    font-size: 16px;
                    width: 100px;
                `}
            >
                {visitor.name}
            </p>
            {sendState === "finish" && (
                <p
                    css={css`
                        width: 100px;
                        font-size: 16px;
                    `}
                >
                    {visitorInfo.guideChannel.toLowerCase()}
                </p>
            )}
            {!(sendState === "finish") && (
                <SelectV2
                    showArrow
                    data={["kakao", "email"]}
                    onChange={(value) => {
                        setVisitorInfo({ ...visitorInfo, guideChannel: value === "kakao" ? "KAKAO" : "EMAIL", email: null, phoneNumber: null});
                    }}
                    value={visitorInfo.guideChannel.toLowerCase()}
                    styles={{
                        container: css`
                            width: 100px;
                            background-color: white;
                            padding: 0;
                            &:hover {
                                background-color: white;
                            }
                        `,

                        text: css`
                            font-size: 16px;
                            font-weight: 500;
                        `,
                    }}
                />
            )}
            <InputField
                value={visitorInfo.guideChannel === "EMAIL" ? (visitorInfo.email ? visitorInfo.email?.toString() : "") : (visitorInfo.phoneNumber ? visitorInfo.phoneNumber?.toString() : "")}
                errorText={visitorInfo.guideChannel === "EMAIL" ? "이메일 형식이 맞지 않습니다." : "전화번호 형식이 맞지 않습니다."}
                onChange={(value) => {
                    if (visitorInfo.guideChannel === "KAKAO") setVisitorInfo({ ...visitorInfo, phoneNumber: value });
                    else setVisitorInfo({ ...visitorInfo, email: value });
                }}
                type={sendState === "finish" ? "disabled" : isRightContact() ? "default" : "error"}
                styles={{
                    container: css`
                        width: 200px;
                    `,
                    input: css`
                        font-size: 15px;
                        height: 40px;
                        padding: 0px 18px;
                    `,
                }}
            />
            {sendState === "finish" && <LoadingBtn>전송완료</LoadingBtn>}
            {sendState === "loading" && (
                <LoadingBtn>
                    <div
                        css={css`
                            display: flex;
                            @keyframes lds-dual-ring {
                                0% {
                                    transform: rotate(0deg);
                                }
                                100% {
                                    transform: rotate(360deg);
                                }
                            }
                            animation: lds-dual-ring 1.2s linear infinite;
                        `}
                    >
                        <Icon name="change" color={theme.neutral200} />
                    </div>
                </LoadingBtn>
            )}
            {sendState === "not_finish" && (
                <Button
                    onClick={async () => {
                        setSendState("loading");
                        
                        const res = await reSendGuideMessage(visitorInfo);
                        if (res.success) {
                            if (res.data?.isGuideMessageSent) {
                                onChange();
                                setSendState("finish");
                            } else setSendState("not_finish");
                        } else {
                            setSendState("not_finish");
                        }
                    }}
                    title="재전송"
                    type="secondary"
                    styles={{
                        container: css`
                            width: 87px;
                            height: 42px;
                        `,
                        text: css`
                            font-size: 14px;
                        `,
                    }}
                />
            )}
            {sendState === "not_finish" && <ErrorText>{visitor.guideMessageErrorCode}</ErrorText>}
        </UserItemContainer>
    );
};

const HeaderContainer = styled.div`
    display: flex;
    padding: 14px 0;
    gap: 10px;
    align-items: center;
    border-bottom: 1px solid ${theme.neutral100};
    margin-top: 40px;
`;

const HeaderItem = styled.p<{ width: number }>`
    color: ${theme.neutral400};
    font-size: 14px;
    font-weight: 500;
    width: ${(props) => props.width + "px"};
`;
export const Header = () => {
    return (
        <HeaderContainer>
            <HeaderItem width={100}>이름</HeaderItem>
            <HeaderItem width={100}>안내 채널</HeaderItem>
            <HeaderItem width={300}>연락처</HeaderItem>
            <HeaderItem width={130}>실패 원인</HeaderItem>
        </HeaderContainer>
    );
};
